import { Component, useEffect, useState, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ToastProvider, TooltipProvider } from '@/components/ui'
import { useCustomerAuthStore } from './stores/auth'
import { CustomerLayout } from './components/CustomerLayout'
import { CustomerLoginPage } from './pages/CustomerLoginPage'
import { CustomerAuthCallbackPage } from './pages/CustomerAuthCallbackPage'
import { CustomerHomePage } from './pages/CustomerHomePage'
import { CustomerSecurityStatusPage } from './pages/CustomerSecurityStatusPage'
import { CustomerContractsPage } from './pages/CustomerContractsPage'
import { CustomerTicketsPage } from './pages/CustomerTicketsPage'
import { CustomerTicketDetailPage } from './pages/CustomerTicketDetailPage'
import { CustomerTicketCreatePage } from './pages/CustomerTicketCreatePage'
import { CustomerAccessMatrixPage } from './pages/CustomerAccessMatrixPage'
import { CustomerEmployeesPage } from './pages/CustomerEmployeesPage'
import { CustomerDocumentsListPage } from './pages/CustomerDocumentsListPage'
import { CustomerDocumentDetailPage } from './pages/CustomerDocumentDetailPage'
import { CustomerOposPage } from './pages/CustomerOposPage'

/**
 * Faengt React-Render-Fehler innerhalb des Kundenportals ab und zeigt eine
 * lesbare Fehlerseite statt einer komplett weissen Seite. Ohne Boundary
 * crashed React den ganzen Render-Tree weg, sobald irgendwo ein Property-
 * Zugriff auf undefined fehlschlaegt.
 */
class CustomerErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, fontFamily: 'system-ui, sans-serif', maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ color: '#b91c1c', marginBottom: 8 }}>Im Kundenportal ist ein Fehler aufgetreten</h2>
          <p style={{ color: '#374151', marginBottom: 16 }}>
            Bitte die Seite neu laden. Falls der Fehler bestehen bleibt, kontaktiere unser Service-Team.
          </p>
          <pre style={{
            whiteSpace: 'pre-wrap',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: 12,
            fontSize: 12,
            color: '#7f1d1d',
            maxHeight: 320,
            overflow: 'auto',
          }}>
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
          <button
            onClick={() => { this.setState({ error: null }); window.location.reload() }}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              background: '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Seite neu laden
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

function useAuthHydrated() {
  const [hydrated, setHydrated] = useState(() => useCustomerAuthStore.persist.hasHydrated())
  useEffect(() => {
    const unsub = useCustomerAuthStore.persist.onFinishHydration(() => setHydrated(true))
    if (useCustomerAuthStore.persist.hasHydrated()) setHydrated(true)
    return unsub
  }, [])
  return hydrated
}

function Private({ children }: { children: React.ReactNode }) {
  const accessToken = useCustomerAuthStore((s) => s.accessToken)
  const hydrated = useAuthHydrated()
  if (!hydrated) return null
  return accessToken ? <>{children}</> : <Navigate to="/login" replace />
}

function LayoutGuard() {
  return (
    <Private>
      <CustomerLayout />
    </Private>
  )
}

export function CustomerApp() {
  return (
    <CustomerErrorBoundary>
    <TooltipProvider>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<CustomerLoginPage />} />
            <Route path="/auth/callback" element={<CustomerAuthCallbackPage />} />

            <Route element={<LayoutGuard />}>
              <Route path="/sicherheitsstatus" element={<CustomerSecurityStatusPage />} />
              <Route path="/vertraege" element={<CustomerContractsPage />} />

              <Route path="/tickets" element={<CustomerTicketsPage />} />
              <Route path="/tickets/neu" element={<CustomerTicketCreatePage />} />
              <Route path="/tickets/:id" element={<CustomerTicketDetailPage />} />

              <Route path="/zugriffsmatrix" element={<CustomerAccessMatrixPage />} />
              <Route path="/mitarbeiter" element={<CustomerEmployeesPage />} />
              <Route path="/compliance" element={<Navigate to="/zugriffsmatrix" replace />} />

              <Route path="/dokumente/angebote" element={<CustomerDocumentsListPage title="Angebote" endpoint="/sap/quotations" detailBase="/dokumente/angebote" />} />
              <Route path="/dokumente/angebote/:docEntry" element={<CustomerDocumentDetailPage title="Angebot" endpoint="/sap/quotations" />} />

              <Route path="/dokumente/auftraege" element={<CustomerDocumentsListPage title="Aufträge" endpoint="/sap/orders" detailBase="/dokumente/auftraege" />} />
              <Route path="/dokumente/auftraege/:docEntry" element={<CustomerDocumentDetailPage title="Auftrag" endpoint="/sap/orders" />} />

              <Route path="/dokumente/lieferscheine" element={<CustomerDocumentsListPage title="Lieferscheine" endpoint="/sap/deliveries" detailBase="/dokumente/lieferscheine" />} />
              <Route path="/dokumente/lieferscheine/:docEntry" element={<CustomerDocumentDetailPage title="Lieferschein" endpoint="/sap/deliveries" />} />

              <Route path="/dokumente/rechnungen" element={<CustomerDocumentsListPage title="Rechnungen" endpoint="/sap/invoices" detailBase="/dokumente/rechnungen" />} />
              <Route path="/dokumente/rechnungen/:docEntry" element={<CustomerDocumentDetailPage title="Rechnung" endpoint="/sap/invoices" />} />

              <Route path="/dokumente/gutschriften" element={<CustomerDocumentsListPage title="Gutschriften" endpoint="/sap/credits" detailBase="/dokumente/gutschriften" />} />
              <Route path="/dokumente/gutschriften/:docEntry" element={<CustomerDocumentDetailPage title="Gutschrift" endpoint="/sap/credits" />} />

              <Route path="/dokumente/opos" element={<CustomerOposPage />} />

              <Route path="/" element={<CustomerHomePage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </TooltipProvider>
    </CustomerErrorBoundary>
  )
}
